import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Button, Input, Label } from '@/ui/primitives';
import type { BandId, NotifyIntent } from '@/domain/bands';
import { formatPhone, isValidPhone, phoneFromParams } from '@/domain/phone';
import type { Dictionary } from '@/i18n';
import { track } from '@/services/analytics';
import type { LeadResult } from '@/services/leads';

type NotifyFormProps = {
  dict: Dictionary;
  intent: NotifyIntent;
  band: BandId;
  inputType: 'cep' | 'geolocation';
  onSubmit: (firstName: string, phone: string) => Promise<LeadResult>;
  onRecorded: () => void;
};

type FormState = 'idle' | 'busy' | 'failed';

export const NotifyForm = ({
  dict,
  intent,
  band,
  inputType,
  onSubmit,
  onRecorded,
}: NotifyFormProps) => {
  /* The campaign link can carry the number, so the field starts filled and the lead only
     confirms it. Read once, on mount: this page never navigates. */
  const [prefilled] = useState(() => phoneFromParams(window.location.search));
  const [name, setName] = useState('');
  const [phone, setPhone] = useState(() => (prefilled === null ? '' : formatPhone(prefilled)));
  const [state, setState] = useState<FormState>('idle');
  const [invalid, setInvalid] = useState<'name' | 'phone' | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (name.trim() === '') {
      setInvalid('name');
      return;
    }
    if (!isValidPhone(phone)) {
      setInvalid('phone');
      return;
    }
    setInvalid(null);
    setState('busy');
    const result = await onSubmit(name, phone);
    if (result === 'failed') {
      setState('failed');
      return;
    }
    setState('idle');
    /* Reported after the survey accepted it, never before. An event fired on the press
       would count leads that were never recorded anywhere. */
    track('sf_notify_requested', {
      intent,
      band,
      input_type: inputType,
      prefilled: prefilled !== null,
    });
    onRecorded();
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="notify-name" className="text-sm">
          {dict.notify.nameLabel}
        </Label>
        <Input
          id="notify-name"
          name="notify-name"
          type="text"
          autoComplete="given-name"
          placeholder={dict.notify.namePlaceholder}
          value={name}
          hasError={invalid === 'name'}
          errorMessage={invalid === 'name' ? dict.notify.invalidName : false}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setName(event.target.value);
            setInvalid(null);
          }}
          className="h-11"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="notify-phone" className="text-sm">
          {dict.notify.phoneLabel}
        </Label>
        <Input
          id="notify-phone"
          name="notify-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder={dict.notify.phonePlaceholder}
          value={phone}
          hasError={invalid === 'phone'}
          errorMessage={invalid === 'phone' ? dict.notify.invalidPhone : false}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setPhone(formatPhone(event.target.value));
            setInvalid(null);
          }}
          className="h-11"
        />
      </div>

      <Button type="submit" disabled={state === 'busy'} className="h-11 w-full text-base font-bold">
        {/* No neon yellow here. AGENTS.md spends the accent once, on the one action the
            page exists for, and this button sits inside a dialog the lead already chose
            to open. */}
        {state === 'busy' ? dict.notify.busy : dict.notify.submit}
      </Button>

      {state === 'failed' ? (
        <p role="alert" className="text-sm font-medium text-red-600">
          {dict.notify.failed}
        </p>
      ) : null}

      <p className="text-[11px] text-grey-800">{dict.notify.privacy}</p>
    </form>
  );
};
